import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { Assembler, classDerivedDeviceEnabledTypes } from '@iotv/datamodel';
import { DeviceClassName, GatewayClassName, GroupClassName, UserClassName, getObjectHash, objectsMatch, objectsMatchDebug, } from '@iotv/datamodel-core';
import { AppValueTypes } from '@iotv/iotv-v3-types';
import { Alert, ButtonGroup, Snackbar, Stack, ThemeProvider, Typography } from '@mui/material';
import { GridCellEditStopReasons, GridCellModes, useGridApiRef, } from '@mui/x-data-grid';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { v4 } from 'uuid';
import { DeleteDialog } from '../../../../../ArbitrationObjects/DeletionArbitrator/DeleteDialog';
import { deletionArbitrator } from '../../../../../ArbitrationObjects/DeletionArbitrator/deletionArbitrator';
import { AppMainViewContext } from '../../../../../ContextProviders/AppContextProvider';
import useQuestion from '../../../../../Popup/useQuestion';
import { TabularDataGrid } from '../../../../../components/factories/DataGrid';
import { isDeviceEnabledType } from '../../../../../data/TypeHelpers';
import { deleteObject } from '../../../../../data/daoFunctions/daoFunctions';
import { useApplicationDataQueries } from '../../../../../hooks/useApplicationDataQueries';
import { Severity, ShowDefinedEntriesOnlyOpts, } from '../../../../../types';
import { ComponentMode } from '../../../../../types/AppTypes';
import { getGroupHierarchy } from '../GroupHierarchy/getGroupHierarchy';
import { objectCardTheme } from '../cosmetics/objectCardTheme';
import { getDeleteButton, getResetButton, getSaveButton } from './Controls/buttons';
import { EntityStateContextSubCard } from './EntityStateContextSubCard';
import { handleDeleteDevice, handleUpdateDevice } from './SpecificTypes/deviceFns';
import { handleDeleteGateway } from './SpecificTypes/gatewayFns';
import { handleDeleteUser } from './SpecificTypes/userFns';
import { getEditCellRenderer } from './cellEditRenderers';
import { getViewCellRenderer } from './cellViewRenderers';
import { getObjectDifferences } from './util';
import { valueSetters } from './valueSetters';
const debug = process.env.REACT_APP_DEBUG && false;
const defaultHasBeenEditedFn = (matchedPrimary, localObject) => {
    return !(objectsMatch(matchedPrimary, localObject) && objectsMatch(localObject, matchedPrimary));
};
/**
 * Keeps a copy of the input object, constructs table and allows editing of that objects userAttributes in the table.
 * WARN: If basic editing is all you need, just use {@link ./DetailCard.tsx | <DetailCard>}
 *
 * This hook returns the table components, but also exposes the local object copy, plus some control attributes
 * @returns An object containing the local object, A setter for the local object, isNewObject - A control variable set to true if the object is being created for the first time, detailCardComponents - a list of components that can render the table
 */
export default function useDetailCard({ matchedPrimary, contextObjectForDetailCard, postTransactionCallback, contextObject, subContextCustomer, mostProximateCustomer, transactionFunctions, viewDefinition: viewDefinitionIn, title, userFunctions, definedEntriesOnlyOpt = ShowDefinedEntriesOnlyOpts.Toggle, allowedActions, resetCallback, hasBeenEditedFn, customButtonFns, isNewObjectIn, showContextSubCard = true, injectedValidationMap = {}, }) {
    'use no memo';
    // Error: Cannot access refs during render
    debug && console.log('Detail Card rerendering', matchedPrimary);
    const [localObject, setLocalObject] = useState(Assembler.getInstance({ ...matchedPrimary }));
    const viewDefinitionRef = useRef(viewDefinitionIn);
    const viewDefinition = viewDefinitionRef.current;
    // Conditional mutation of viewKeyDefinition based on current object
    Object.entries(viewDefinition).forEach(([k, def]) => {
        if (def.dependsOn) {
            debug && console.log(`${k} has depends on in ${localObject.sk}`);
            if (localObject) {
                const partialViewKeyDefinition = def.dependsOn(localObject);
                // if present, override default attributes
                if (partialViewKeyDefinition) {
                    viewDefinitionRef.current[k] = { ...viewDefinition[k], ...partialViewKeyDefinition };
                }
            }
        }
    });
    const navigate = useNavigate();
    const apiRef = useGridApiRef();
    const [editing, setEditing] = useState([]);
    useEffect(() => {
        const callback = (params) => {
            setEditing(Object.values(params)
                .map((r) => r.kv.mode)
                .filter((m) => m === GridCellModes.Edit));
        };
        return apiRef.current?.subscribeEvent?.('cellModesModelChange', callback);
    }, [apiRef]);
    const [{ contextUser, contextCustomer, applicationData, roleArbitrationInstance }] = useContext(AppMainViewContext);
    const isCurrentUser = matchedPrimary.id === contextUser?.id;
    /* Mode gets overwritten by being view only */
    const mode = allowedActions?.edit === true ? ComponentMode.EDIT : ComponentMode.VIEW;
    const definedEntriesOnly = definedEntriesOnlyOpt !== ShowDefinedEntriesOnlyOpts.No;
    // WARN: This may cause problems adding new objects.
    // Theoretically you should not be adding new objects if you're view only anyway, but still
    //useEffect(() => {
    //  if (isNewObject) {
    //    debug && console.log('recognised new object')
    //    setMode(ComponentMode.EDIT)
    //  }
    //}, [isNewObject])
    const { isNewObject: detectedNewObject } = useApplicationDataQueries({ item: matchedPrimary });
    const isNewObject = isNewObjectIn ?? detectedNewObject;
    const [deleting, setDeleting] = useState(false);
    const [resolve, setResolve] = useState(() => () => { });
    const [confirmation, setConfirmation] = useState({ title: 'Confirm?', question: 'Are you sure?' });
    const { Dialogue, setOpen } = useQuestion({ ...confirmation, result: (r) => resolve(r) });
    const getConfirmation = (props) => {
        setConfirmation(props);
        setOpen(true);
        return new Promise((resolve) => setResolve(() => resolve));
    };
    const handleDeleteViaConsumerAPI = async () => {
        const res = await deleteObject(localObject);
        const { err, data } = res;
        if (err) {
            userFunctions.setUserMessage({
                id: v4(),
                content: `Failed to delete ${localObject.name}${debug ? ` ${err.message}` : ''}`,
                label: 'Consumer Data API',
                type: 'MessageBar',
                severity: Severity.error,
            });
        }
        if (data) {
            userFunctions.removeObjectsFromState([localObject]);
            userFunctions.setUserMessage({
                id: v4(),
                content: `Deleted ${localObject.name}${debug ? ` (${data.sk})` : ''}`,
                label: 'Consumer Data API',
                type: 'MessageBar',
                severity: Severity.success,
            });
        }
        return res;
    };
    /** Special per type grumble grumble... but it works for now */
    const handleDeleteLocalObject = useMemo(() => {
        let fn = handleDeleteViaConsumerAPI;
        if (localObject.type === UserClassName) {
            fn = () => handleDeleteUser(localObject, userFunctions);
        }
        else if (localObject.type === DeviceClassName) {
            if (contextCustomer !== undefined && contextUser !== undefined)
                fn = () => handleDeleteDevice(localObject, userFunctions, contextCustomer, contextUser, getConfirmation);
        }
        else if (localObject.type === GatewayClassName) {
            if (contextCustomer !== undefined && contextUser !== undefined)
                fn = () => handleDeleteGateway(localObject, userFunctions, contextCustomer, contextUser);
        }
        return fn;
    }, [localObject, userFunctions, contextCustomer?.sk, contextUser?.sk]);
    const deleteAndClose = async () => {
        setDeleting(true);
        const deleteRes = await handleDeleteLocalObject();
        if (!deleteRes.err) {
            setDeleteDialogOpen(false);
            navigate(-1);
        }
        setDeleting(false);
    };
    const [saving, setSaving] = useState(false);
    const defaultHandleSaveObject = () => {
        debug && console.log('DetailCard calling possibly mutated saveObject fn', localObject);
        // WARN: This could have been mutated. Common mutations are:
        // - Listerlizer mutates saveObject so it updates app state and closes the new obj dialog
        // - UserDetailCard mutates saving an existing User to update roles and admin-ness
        return transactionFunctions.saveObject(localObject);
    };
    const handleSaveObject = useMemo(() => {
        return async () => {
            setSaving(true);
            if (isNewObject || !contextCustomer || !contextUser) {
                const res = await defaultHandleSaveObject();
                setSaving(false);
                return res;
            }
            switch (localObject.type) {
                case DeviceClassName: {
                    const res = await handleUpdateDevice(localObject, userFunctions, contextCustomer, contextUser);
                    setSaving(false);
                    return res;
                }
            }
            const res = await defaultHandleSaveObject();
            setSaving(false);
            return res;
        };
    }, [localObject, transactionFunctions, isNewObject]);
    const [cellModesModel, setCellModesModel] = useState(undefined);
    /** Entity extending `DeviceEnabledType` only */
    const [entityContext, setEntityContext] = useState(null);
    const [validationMap, setValidationMap] = useState({});
    const [userCanDelete, setUserCanDelete] = useState(false);
    const [requiredValueMap, setRequiredValuesMap] = useState({});
    const [snackbar, setSnackbar] = useState(null);
    // States for handling delete dialogue:
    const [deletionWarningMessage, setDeletionWarningMessage] = useState('');
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const objectHash = getObjectHash(matchedPrimary);
    useEffect(() => {
        /**
         * 2024 07 as not rerendering on externally invoked changes (e.g. map changes location )
         * the issue here is that external changes mean that the diff is nothing and save is disabled
         */
        debug && console.log('Detail Card used effect on object hash');
        if (matchedPrimary) {
            const clone = Assembler.getInstance(matchedPrimary);
            debug && console.log('useDetalCard', 'Differences:', getObjectDifferences(matchedPrimary, clone, 'matchedPrimary', 'clone'));
            setLocalObject(clone);
        }
    }, [objectHash]);
    const fetchGroupHierarchy = async () => {
        const res = await getGroupHierarchy({ item: localObject, groupDefiningObjectTypeId: 'Customer' });
        debug && console.log(`DetailCard fetchGroupHierarchy res`, res);
        if (res.data) {
            const groupCustomerNodeEdgeMapper = res.data;
            userFunctions.addObjectsToState(groupCustomerNodeEdgeMapper.getContainedNodesAndEdges());
        }
        if (res.err) {
            console.error(`Err: DetailCard fetchGroupHierarchy`, res.err.message);
        }
    };
    const requiresGroupHierarchy = ['Customer', GroupClassName, UserClassName, DeviceClassName, GatewayClassName, ...classDerivedDeviceEnabledTypes].includes(localObject.type);
    /**
     * Automatically populates `appState` with group customer hierarchy if no most proximate customer is available
     *
     * If the detail card is being used to display a new object in a modal launched from a context menu, adding fetched hierachy to app state will cause a rerender and close the modal
     * To avoid this, the hook does not fetch on new object
     *
     * If the detail card is opened directly from a URL, the object will not be in `appState` because it will be auto-queried by `ThingView`. `isNewObject` will be null. Hook also does not fetch
     * */
    useEffect(() => {
        debug && console.log('Detail card used effect on', matchedPrimary.name);
        if (requiresGroupHierarchy && !mostProximateCustomer && isNewObject === false) {
            debug && console.log(`DetailCard Fetching group hierachy`, { requiresGroupHierarchy, mostProximateCustomer, isNewObject, matchedPrimary });
            fetchGroupHierarchy();
        }
        else {
            debug && console.log(`DetailCard Fetching NOT group hierachy`, { requiresGroupHierarchy, mostProximateCustomer, isNewObject, matchedPrimary });
        }
        /** rectification of #SeeTableNotUpdated */
        if (matchedPrimary.sk !== localObject.sk) {
            setLocalObject(matchedPrimary);
        }
    }, [matchedPrimary?.sk, isNewObject]);
    const requiredValuesNotPopulated = !!Object.values(requiredValueMap).find((b) => b === true);
    // debug && console.log(`DetailCard requiredValuesNotPopulated`, { requiredValuesNotPopulated, requiredValueMap })
    const edited = matchedPrimary && localObject && (hasBeenEditedFn ? hasBeenEditedFn(matchedPrimary, localObject) : defaultHasBeenEditedFn(matchedPrimary, localObject));
    const hasValidationErrors = !!Object.values({ ...validationMap, ...(injectedValidationMap ?? {}) }).find((v) => v !== undefined);
    // debug && console.log('DetailCard may rerender?', { incomingObjectDiffHash, matchedPrimary, referenceObject });
    const nameLabel = title ?? localObject.name ?? `New ${localObject.type}`;
    const keys = Object.keys(viewDefinition);
    const rowsPerPage = keys.length + 1;
    const validationMessageFn = (change) => {
        setValidationMap((vm) => {
            debug && console.log('validationMessageFn', 'change', structuredClone(change));
            const addedEntries = Object.entries(change).filter(([_, v]) => v !== undefined);
            debug && console.log('validationMessageFn', 'addedEntries', addedEntries);
            const removedEntries = Object.entries(change)
                .filter(([_, v]) => v === undefined)
                .map(([k]) => k);
            debug && console.log('validationMessageFn', 'removedEntries', removedEntries);
            const clone = structuredClone(Object.fromEntries(Object.entries(vm).filter(([k]) => !removedEntries.includes(k) && !viewDefinition[k].hidden)));
            debug && console.log('validationMessageFn', 'clone', clone);
            const result = structuredClone({ ...clone, ...change });
            debug && console.log('validationMessageFn', 'result', result);
            return result;
        });
    };
    const onProcessRowUpdateError = (error) => {
        debug && setSnackbar({ children: error.message, severity: 'error' });
    };
    const setValueCellEditView = (k, mode) => {
        setCellModesModel({ ...cellModesModel, [k]: { kv: { mode } } });
    };
    useEffect(() => {
        const setCanDelete = async () => {
            if (applicationData && contextCustomer && contextUser) {
                const customerToRemoveFrom = mostProximateCustomer ?? contextCustomer;
                if (customerToRemoveFrom) {
                    const { deletable: canBeDeleted, message } = await deletionArbitrator({ matchedPrimary, contextUser });
                    setUserCanDelete(canBeDeleted);
                    setDeletionWarningMessage(message);
                }
                else {
                    /** this is not correct deletionArbitrator does not take into account context customer */
                    userFunctions.setUserMessage({
                        id: v4(),
                        content: `No resolved customer to delete ${matchedPrimary.name} from `,
                        label: `App State Error`,
                        type: 'MessageBar',
                        severity: Severity.error,
                    });
                }
            }
            else {
                debug && console.log(`Detail Card canDelete did not run`, { applicationData, contextCustomer });
            }
        };
        /* Un predicating this will generate a context list for all objectTypeIds, currently just DeviceEnabledTypes */
        if (isNewObject === false && matchedPrimary && (isDeviceEnabledType(matchedPrimary) || !['User', 'Group'].includes(matchedPrimary.type))) {
            const singleNodeEdgeMapper = applicationData?.extractSingleNodeEdgeMapper(matchedPrimary);
            if (singleNodeEdgeMapper) {
                setEntityContext(singleNodeEdgeMapper);
            }
        }
        setCanDelete();
    }, [matchedPrimary.sk, applicationData, contextCustomer?.sk, contextUser?.sk, isNewObject]);
    const handleCloseSnackbar = () => setSnackbar(null);
    /**
     * Return updated row if valid
     * If validation fails, return `undefined` to restrict the user from exiting edit mode
     * https://mui.com/x/react-data-grid/editing/persistence/
     */
    const processRowUpdate = (updatedRow, oldRow, params) => {
        //const reluctantlySkipValidation: boolean = !!showVariantControls // I want to deprecate this... but can I?
        debug && console.log(`DetailCard processRowUpdate`, { updatedRow, validationMap, hasValidationErrors });
        // It might seem strange to apply the update to the local object, then again to the clone, but it's because
        // edits can be written by the constructor called in the Assembler. Re-writing the value means that our value will persist in the clone even if updated in the constructor. Now this might not be desirable, but if you really want something to not be edited you can set it so in the object's definition.
        const { k, v } = updatedRow.kv;
        const clone = Assembler.getInstance({ ...localObject, [k]: v });
        if (clone === undefined)
            return updatedRow;
        clone[k] = v;
        setLocalObject(clone);
        if (!hasValidationErrors) {
            debug &&
                setSnackbar({
                    children: `Updated ${k} to ${JSON.stringify(v)} with diff ${localObject ? JSON.stringify(objectsMatchDebug(localObject, clone)) : 'NO LOCAL OBJECT'} but not saved`,
                    severity: 'success',
                });
        }
        else {
            const { k, v } = updatedRow.kv;
            debug &&
                setSnackbar({
                    children: `Not updating ${k} due to validation errors: ${Object.entries(validationMap)
                        .map(([k, v]) => `${v?.content}`)
                        .join(', ')}`,
                    severity: 'error',
                });
        }
        return updatedRow;
    };
    const columnValueGetter = (value, row, column, apiRef) => {
        const viewKeyDefinition = viewDefinition[value];
        let keyNameLabel = viewKeyDefinition ? viewKeyDefinition.label : value;
        if (viewKeyDefinition?.unit) {
            keyNameLabel += ` (${viewKeyDefinition.unit})`;
        }
        return keyNameLabel;
    };
    /** sets required attribute key column values to red if not populated, and appends an asterisk to show required */
    const columnValueCellRenderer = (p) => {
        const kv = p.row.kv;
        if (kv) {
            const viewKeyDefinition = viewDefinition[kv.k];
            if (viewKeyDefinition?.required) {
                let color = undefined;
                // CRA — TODO 2025 05: finish implementing the rest of the validation hints
                switch (viewKeyDefinition.type) {
                    case AppValueTypes.NUMBER:
                        if (kv.v === null || isNaN(kv.v))
                            color = 'red';
                        break;
                    case AppValueTypes.DATE:
                        break;
                    case AppValueTypes.STRING:
                        if (kv.v === undefined || kv.v?.length === 0)
                            color = 'red';
                        break;
                    case AppValueTypes.BOOLEAN:
                        if (kv.v !== true && kv.v !== false)
                            color = 'red';
                        break;
                    case AppValueTypes.ENUM:
                        if (kv.v === undefined)
                            color = 'red';
                        break;
                    case AppValueTypes.TIMESTAMP_MS:
                        if (kv.v === undefined)
                            color = 'red';
                        break;
                    case AppValueTypes.TIMESTAMP_SEC:
                        if (kv.v === undefined)
                            color = 'red';
                        break;
                    case AppValueTypes.TIMESTAMP_STRING_SEC:
                        if (kv.v === undefined)
                            color = 'red';
                        break;
                    case AppValueTypes.OBJECT:
                        if (kv.v === undefined)
                            color = 'red';
                        break;
                }
                return _jsxs("span", { color: color, children: [p.value, "*"] });
            }
            else {
                return undefined;
            }
        }
        else
            return undefined;
    };
    const columnMap = generateColumnMap(mode, columnValueGetter, columnValueCellRenderer, viewDefinition, validationMessageFn, validationMap);
    const getReadOnlyCellModel = (viewDefinition) => viewDefinition ? Object.fromEntries(Object.keys(viewDefinition).map((k) => [k, { kv: { mode: GridCellModes.View } }])) : {};
    useEffect(() => {
        setCellModesModel(getReadOnlyCellModel(viewDefinition));
    }, [viewDefinition]);
    const handleCloseDeleteDialog = () => {
        setDeleteDialogOpen(false);
    };
    const hideFooter = keys.length < rowsPerPage;
    const onCellSingleClick = (params, event) => {
        debug && console.log('DetailCard onCellSingleClick', params, event);
        if (!params.isEditable) {
            return;
        }
        // Ignore portal
        if (event.target.nodeType === 1 && !event.currentTarget.contains(event.target)) {
            return;
        }
        const viewKeyDefinition = viewDefinition[params.id];
        if (!viewKeyDefinition) {
            debug && console.log(`WARN DetailCard onCellSingleClick no viewKeyDefintion for ${params.id}`, viewDefinition);
        }
        else {
            if (viewKeyDefinition.editable && mode === ComponentMode.EDIT) {
                setValueCellEditView(params.id, GridCellModes.Edit);
            }
            else {
                debug && console.log(`DetailCard onCellSingleClick ${params.id} not editable`, viewKeyDefinition);
            }
        }
    };
    /**
     * 2024 04 in some cases, localObject is not being updated when matched primary changes,
     * symptoms are: table does not update but canaries do
     */
    const data = getData(viewDefinition, localObject, definedEntriesOnly);
    const dataGridProps = generateDataGridProps({
        apiRef,
        data,
        columnMap,
        cellModesModel,
        hideHeaders: true,
        hideFooter,
        disabled: saving,
        onCellSingleClick,
        viewDefinition,
        rowsPerPage,
        processRowUpdate,
        onProcessRowUpdateError,
        setValueCellEditView,
    });
    debug && console.log(`Detail card datagrid data is`, dataGridProps.rows);
    const handleReset = () => {
        setLocalObject(Assembler.getInstance({ ...matchedPrimary }));
        if (resetCallback)
            resetCallback();
    };
    const handleDelete = async () => {
        if (applicationData) {
            setDeleteDialogOpen(true);
        }
    };
    const showDeleteButton = allowedActions?.delete === true && !isCurrentUser && !isNewObject; // TODO: isCurrentUser should really not be in this card
    const displayControls = ![ComponentMode.VIEW, ComponentMode.READONLY, ComponentMode.NOEDIT].includes(mode);
    const validationValues = Object.values({ ...validationMap, ...(injectedValidationMap ?? {}) }).filter((kv) => kv !== undefined);
    const validationAlert = 0 < validationValues.filter((v) => v.type !== 'required').length ? (_jsx(Alert, { severity: "info", sx: { paddingBlock: 0, alignItems: 'center' }, children: validationValues
            .filter((v) => v.type !== 'required')
            .map((kv) => kv.content)
            .join(', ') })) : (_jsx(_Fragment, {}));
    const detailCardControls = (_jsxs(_Fragment, { children: [!(mode === ComponentMode.VIEW) && (nameLabel || showDeleteButton || displayControls || isCurrentUser) && (_jsx(Stack, { direction: { xs: 'column', md: 'row' }, gap: 1, children: !(displayControls || isCurrentUser) ? (_jsxs(_Fragment, { children: [_jsx(Typography, { title: typeof nameLabel === 'string' ? nameLabel : nameLabel }), validationAlert] })) : (_jsxs(_Fragment, { children: [validationAlert, _jsx(Stack, { flexGrow: 1, direction: 'row', justifyContent: 'right', alignItems: 'center', children: _jsxs(ButtonGroup, { children: [customButtonFns?.map((buttonFn) => buttonFn()), !isNewObject && getResetButton({ edited, disabled: saving, handleCancelEdit: handleReset }), getSaveButton({
                                        edited,
                                        disabled: 0 < editing.length || saving,
                                        loading: saving,
                                        handleSaveObject,
                                        hasValidationErrors,
                                        requiredValuesNotPopulated,
                                    }), showDeleteButton && getDeleteButton({ handleDelete, disabled: deleting || saving, loading: deleting, isDeletable: userCanDelete })] }) })] })) })), DeleteDialog({
                handleClose: handleCloseDeleteDialog,
                handleDelete: deleteAndClose,
                open: deleteDialogOpen,
                warningMessage: deletionWarningMessage,
                candelete: userCanDelete,
            }), Dialogue] }));
    const detailCardTable = (_jsx(_Fragment, { children: dataGridProps && (_jsx(ThemeProvider, { theme: objectCardTheme, children: _jsxs(Stack, { gap: 1, children: [_jsx(TabularDataGrid, { style: { fontSize: 'inherit' }, ...dataGridProps }), showContextSubCard && entityContext && _jsx(EntityStateContextSubCard, { apexNode: matchedPrimary, context: entityContext })] }) })) }));
    const detailCardComponent = (_jsxs(Stack, { gap: 1, children: [detailCardControls, debug && (_jsxs(Stack, { children: [debug && _jsx("div", { children: `${matchedPrimary.name} is ${isNewObject ? '' : 'not'} a new object` }), debug && _jsx("div", { children: `Context customer for new objects is ${contextCustomer?.name}` }), debug && _jsx("div", { children: `Sub Context customer for new objects is ${subContextCustomer?.name}` })] })), detailCardTable, snackbar !== null && (_jsx(Snackbar, { open: true,
                anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
                onClose: handleCloseSnackbar,
                autoHideDuration: 6000, children: _jsx(Alert, { ...snackbar, elevation: 6, onClose: handleCloseSnackbar }) }))] }));
    return {
        /** Reference to the local copy of the object */
        localObject,
        /** Setter for the local copy of the object so you can affect it externally */
        setLocalObject,
        /** Control var set to true if the detail card is being used on an object that doesn't exist yet */
        isNewObject,
        /** Control mode for the current edit mode of the detail card */
        editMode: allowedActions?.edit === true ? ComponentMode.EDIT : ComponentMode.VIEW,
        /** Components for the detailCard */
        detailCardComponents: {
            /** Just the validation warnings and save buttons*/
            controls: detailCardControls,
            /** The datagrid */
            table: detailCardTable,
            /** Everything put together */
            wholeComponent: detailCardComponent,
        },
    };
}
export const generateDataGridProps = ({ apiRef, cellModesModel, columnMap, data, hideFooter, hideHeaders, disabled = false, onCellSingleClick, onProcessRowUpdateError, processRowUpdate, rowsPerPage, setValueCellEditView, viewDefinition, }) => {
    const rowToGridRow = ({ k, v }) => ({ id: k, k, kv: { k, v } });
    const columns = Object.values(columnMap);
    const rows = data.map(rowToGridRow);
    const dataGridProps = {
        apiRef,
        checkboxSelection: false,
        cellModesModel,
        columns: columns,
        columnHeaderHeight: hideHeaders ? 0 : undefined,
        density: 'compact',
        disableRowSelectionOnClick: disabled,
        hideFooter,
        hideFooterSelectedRowCount: true,
        getCellClassName: (p) => {
            const definition = viewDefinition[p.value?.k ?? 'never'];
            if (definition)
                return definition.editable === true ? 'editable' : 'disabled';
            return '';
        },
        isRowSelectable: (p) => viewDefinition[p.row.k ?? 'never']?.editable === true,
        isCellEditable: (p) => viewDefinition[p.value?.k ?? 'never']?.editable === true,
        keepNonExistentRowsSelected: true,
        // loading: queryHook.inFlight,
        initialState: {
            pagination: {
                paginationModel: {
                    pageSize: rowsPerPage,
                },
            },
        },
        processRowUpdate,
        onProcessRowUpdateError,
        // rowCount: LastEvaluatedKey ? itemsCount * (LastEvaluatedKeys.length + 1) + rowsPerPage + 1 : itemsCount,
        rows,
        onCellClick: !disabled ? onCellSingleClick : undefined,
        onCellEditStart: (params) => setValueCellEditView(params.id, GridCellModes.Edit),
        onCellEditStop: (params, event) => {
            debug && console.log(`DetailCard stop edit`, params.reason, params);
            if (params.reason === GridCellEditStopReasons.cellFocusOut) {
                event.defaultMuiPrevented = true;
            }
            else {
                // while this cast works, it probably is not the prescribed way of getting the input...
                debug && console.log(`DetailCard stop editing`, params.row, params.formattedValue, event.target.value); // this corresponds to the item
            }
            setValueCellEditView(params.id, GridCellModes.View);
        },
        slotProps: { cell: { onBlur: (event) => debug && console.log('DetailCard focus out', event.target) } },
        slots: { columnHeaders: hideHeaders ? () => null : undefined },
    };
    return dataGridProps;
};
export const generateColumnMap = (mode, columnValueGetter, 
/* Renders the heading column for each row */
columnValueCellRenderer, viewDefinition, validationMessageFn, validationMap) => {
    return {
        k: {
            headerName: 'Key',
            field: 'k',
            flex: 3,
            valueGetter: columnValueGetter,
            renderCell: columnValueCellRenderer,
            editable: false,
            cellClassName: 'dark',
        },
        kv: {
            //{ k: string, kv: KV }, JSX.Element
            headerName: 'Value',
            field: 'kv',
            flex: 8,
            display: 'flex',
            renderCell: (params) => {
                if (params.value) {
                    const { k, v } = params.value;
                    const viewKeyDefinition = viewDefinition[k];
                    if (viewKeyDefinition) {
                        const cellRenderer = getViewCellRenderer({ viewKeyDefinition, validationMessageFn, validationMap });
                        return cellRenderer?.(params) ?? _jsx("div", { children: `No view cell renderer for ${viewKeyDefinition.type}` });
                    }
                    else {
                        return _jsx("div", { children: `No view key definition (view) for ${k}` });
                    }
                }
            },
            renderEditCell: (params) => {
                if (params.value) {
                    const { k, v } = params.value;
                    const viewKeyDefinition = viewDefinition[k];
                    if (viewKeyDefinition) {
                        const cellRenderer = viewKeyDefinition.editable === false
                            ? getViewCellRenderer({ viewKeyDefinition, validationMessageFn, validationMap })
                            : getEditCellRenderer({ viewKeyDefinition, validationMessageFn });
                        return cellRenderer?.(params) ?? _jsx("div", { children: `No cell renderer for ${viewKeyDefinition.type}` });
                    }
                    else {
                        return _jsx("div", { children: `No view key definition for ${k}` });
                    }
                }
                else {
                    return ((params) => _jsx("div", { children: 'nothing edit' }))();
                }
            },
            valueSetter: (value, row) => {
                if (value) {
                    const { k, v } = value;
                    const viewKeyDefinition = viewDefinition[k];
                    if (viewKeyDefinition) {
                        const valueSetter = valueSetters[viewKeyDefinition.type];
                        return valueSetter?.(value, row) ?? valueSetters.string(value, row);
                    }
                    else {
                        valueSetters.string(value, row);
                    }
                }
                else {
                    return _jsx("div", { children: JSON.stringify(value ?? 'nothing') });
                }
            },
            editable: [ComponentMode.EDIT, ComponentMode.CREATE].includes(mode),
        },
    };
};
const filterViewDefinitionKeysByVisible = ([k, keyDef]) => {
    const isVisible = !keyDef.hidden;
    return isVisible;
};
/* Turns data from a local object into key value pairs
 * INFO: handles raw data only. Any formatting is done by the cell renderer, not here.
 * */
export const getData = (viewDefinition, localObject, definedEntriesOnly) => {
    debug && console.log('getting data from local object', localObject);
    const objectEntries = Object.entries(viewDefinition)
        .filter(filterViewDefinitionKeysByVisible)
        .map(([k, keyDef]) => {
        return { k: keyDef.key, v: localObject[k] };
    });
    if (!definedEntriesOnly) {
        Object.entries(localObject)
            .sort(([k1], [k2]) => (k1 < k2 ? 1 : -1))
            .forEach(([k, v]) => {
            const viewKeyDefinition = viewDefinition[k];
            if (viewKeyDefinition) {
                objectEntries.push({ k, v });
            }
        });
    }
    return objectEntries;
};
